<template>
  <div class="calendar-page view active">
    <header class="view-header">
      <h1>Key Dates</h1>
      <p class="subtitle">Important timing windows across tracked commodities, shown as a compact vertical timeline.</p>
    </header>

    <section class="timeline-toolbar">
      <div class="filter-tabs">
        <button :class="['filter-tab', { active: activeFilter === 'all' }]" @click="activeFilter = 'all'">All</button>
        <button :class="['filter-tab gold-tab', { active: activeFilter === 'gold' }]" @click="activeFilter = 'gold'">Gold</button>
        <button :class="['filter-tab silver-tab', { active: activeFilter === 'silver' }]" @click="activeFilter = 'silver'">Silver</button>
        <button :class="['filter-tab copper-tab', { active: activeFilter === 'copper' }]" @click="activeFilter = 'copper'">Copper</button>
      </div>

      <div class="timeline-summary">
        <span class="summary-chip">
          <span class="summary-label">Active</span>
          <strong>{{ activeCount }}</strong>
        </span>
        <span v-if="nextEvent" class="summary-chip">
          <span class="summary-label">Next</span>
          <strong>{{ nextEvent.commodityLabel }} {{ formatMonthDay(nextEvent.startDate) }}</strong>
        </span>
      </div>
    </section>

    <section class="timeline-legend">
      <span class="legend-item"><span class="legend-dot buy"></span>Buy</span>
      <span class="legend-item"><span class="legend-dot watch"></span>Watch</span>
      <span class="legend-item"><span class="legend-dot avoid"></span>Avoid</span>
      <span class="legend-item"><span class="legend-dot active"></span>Active now</span>
    </section>

    <section v-if="groupedEvents.length" class="timeline-shell">
      <div v-for="group in groupedEvents" :key="group.label" class="timeline-group">
        <div class="timeline-month">
          <span class="month-line"></span>
          <span class="month-label">{{ group.label }}</span>
          <span class="month-meta">{{ group.events.length }} {{ group.events.length === 1 ? 'window' : 'windows' }}</span>
        </div>

        <div v-for="(event, index) in group.events" :key="event.id" class="timeline-row">
          <div class="timeline-rail">
            <span :class="['timeline-dot', event.commodity, event.type.toLowerCase(), { active: event.phaseClass === 'active' }]"></span>
            <span v-if="index !== group.events.length - 1" class="timeline-line"></span>
          </div>

          <article :class="['timeline-card', event.type.toLowerCase(), event.phaseClass]">
            <div class="timeline-card-row timeline-card-row--top">
              <div class="timeline-tags">
                <span :class="['commodity-pill', event.commodity]">
                  <span class="pill-dot"></span>
                  {{ event.commodityLabel }}
                </span>
                <span :class="['signal-badge', event.type.toLowerCase()]">{{ event.type }}</span>
                <span :class="['phase-chip', event.phaseClass]">{{ event.phaseLabel }}</span>
              </div>

              <div class="timeline-dates">
                <span class="date-block">{{ formatMonthDay(event.startDate) }}</span>
                <span class="date-arrow">→</span>
                <span class="date-block">{{ formatMonthDay(event.endDate) }}</span>
                <span class="date-span">{{ event.durationDays }}d</span>
              </div>
            </div>

            <div class="timeline-card-row timeline-card-row--bottom">
              <div class="timeline-copy">
                <span class="timeline-copy-primary">{{ event.summary }}</span>
                <span class="timeline-copy-secondary">{{ event.strategies[0] }}</span>
              </div>
              <span class="timeline-relative">{{ event.relativeLabel }}</span>
            </div>
          </article>
        </div>
      </div>
    </section>

    <div v-else class="no-events">
      <p>No timing windows found for this filter.</p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const activeFilter = ref('all');
const TODAY = startOfDay(new Date());

const baseEvents = [
  ...buildEvents('gold', 'Gold', [
    { type: 'AVOID', startDate: '2026-05-29', endDate: '2026-06-11', strategies: ['Venus-Pluto Opposition · Bearish · Score -10'] },
    { type: 'BUY', startDate: '2026-08-02', endDate: '2026-08-07', strategies: ['Jupiter-Venus Sextile EXACT · Score 71 · +1.9% · 82.5% WR'] },
    { type: 'BUY', startDate: '2026-10-19', endDate: '2026-10-27', strategies: ['Jupiter-Venus Sextile EXACT · Score 71 · +1.9% · 82.5% WR'] },
  ]),
  ...buildEvents('silver', 'Silver', [
      { type: 'BUY', startDate: '2026-04-17', endDate: '2026-04-18', strategies: ['Mercury Ingress + Waxing Moon · Score 3.2 · +3-6%'] },
  { type: 'BUY', startDate: '2026-04-24', endDate: '2026-04-27', strategies: ['Venus Ingress + Waxing Moon · Score 3.4 · +3-5%'] },
    { type: 'BUY', startDate: '2026-05-07', endDate: '2026-05-10', strategies: ['Neptune Station + Moon Aquarius · Score 4.7 · +5-9%'] },
    { type: 'BUY', startDate: '2026-05-17', endDate: '2026-05-21', strategies: ['Mercury Ingress + Venus Ingress confluence · Score 4.2 · +5-10%'] },
    { type: 'BUY', startDate: '2026-05-23', endDate: '2026-05-25', strategies: ['Double Mutable + Waxing Moon · Score 3.9 · +4-7%'] },
    { type: 'BUY', startDate: '2026-06-01', endDate: '2026-06-04', strategies: ['Mercury Ingress Cancer + Neptune Station residual + Double Mutable · Score 4.8-6.0 · +4-8%'] },
    { type: 'BUY', startDate: '2026-06-05', endDate: '2026-06-08', strategies: ['Neptune Station + Moon Aquarius Elite · Score 3.5-4.0 · +3-6%'] },
    { type: 'BUY', startDate: '2026-06-13', endDate: '2026-06-15', strategies: ['Venus Ingress Leo + Neptune Station + Double Mutable Gemini · Score 4.0-5.7 · +5-9%'] },
    { type: 'BUY', startDate: '2026-06-16', endDate: '2026-06-22', strategies: ['Waxing Moon + Neptune Station residual · Score 3.0-4.0 · +3-5%'] },
    { type: 'BUY', startDate: '2026-06-23', endDate: '2026-06-28', strategies: ['Double Mutable Gemini+Vir + Neptune Station + Waxing · Score 4.0-6.0 · +4-8%'] },
  ]),
  ...buildEvents('copper', 'Copper', [
    { type: 'BUY', startDate: '2026-04-17', endDate: '2026-04-23', strategies: ['Mars Aries + Venus Taurus + 4 Conjunctions · Score 63 HIGH · +2.8%'] },
    { type: 'WATCH', startDate: '2026-04-24', endDate: '2026-05-07', strategies: ['Venus leaves Taurus Apr 24 — copper signal weakens · Score 25 LOW'] },
    { type: 'BUY', startDate: '2026-05-08', endDate: '2026-05-16', strategies: ['Venus-Mars sextile + Moon Aries/Taurus · Score 40 STANDARD · +2.3%'] },
    { type: 'AVOID', startDate: '2026-06-01', endDate: '2026-06-12', strategies: ['Mars in Taurus (no rulership bonus) + Venus in Cancer · Score 5-15 · ALL LOW'] },
    { type: 'WATCH', startDate: '2026-06-13', endDate: '2026-06-28', strategies: ['Venus in Leo (dignified +10) but Mars still Taurus · Score 15-18 · Still LOW'] },
    { type: 'AVOID', startDate: '2026-06-29', endDate: '2026-06-30', strategies: ['Mars enters Gemini — no rulership · No copper trade in June'] },
  ]),
];

const allEvents = computed(() =>
  baseEvents
    .map((event) => decorateEvent(event))
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
);

const filteredEvents = computed(() => {
  if (activeFilter.value === 'all') return allEvents.value;
  return allEvents.value.filter((event) => event.commodity === activeFilter.value);
});

const groupedEvents = computed(() => {
  const groups = [];
  let currentLabel = '';
  let currentGroup = null;

  for (const event of filteredEvents.value) {
    const label = new Date(event.startDate).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (label !== currentLabel) {
      currentLabel = label;
      currentGroup = { label, events: [] };
      groups.push(currentGroup);
    }
    currentGroup.events.push(event);
  }

  return groups;
});

const activeCount = computed(() => filteredEvents.value.filter((event) => event.phaseClass === 'active').length);
const upcomingEvents = computed(() => filteredEvents.value.filter((event) => event.phaseClass === 'upcoming'));
const activeEvents = computed(() => filteredEvents.value.filter((event) => event.phaseClass === 'active'));
const nextEvent = computed(() => activeEvents.value[0] || upcomingEvents.value[0] || null);

function buildEvents(commodity, commodityLabel, windows) {
  return windows.map((window) => ({
    ...window,
    id: commodity + '-' + window.startDate + '-' + window.endDate,
    commodity,
    commodityLabel,
  }));
}

function decorateEvent(event) {
  const start = startOfDay(new Date(event.startDate));
  const end = startOfDay(new Date(event.endDate));

  return {
    ...event,
    durationDays: diffInDays(start, end) + 1,
    phaseClass: getPhaseClass(start, end),
    phaseLabel: getPhaseLabel(start, end),
    relativeLabel: getRelativeLabel(start, end),
    summary: simplifyStrategy(event.strategies?.[0] || ''),
  };
}

function getPhaseClass(start, end) {
  if (TODAY >= start && TODAY <= end) return 'active';
  if (TODAY < start) return 'upcoming';
  return 'closed';
}

function getPhaseLabel(start, end) {
  if (TODAY >= start && TODAY <= end) return 'Active';
  if (TODAY < start) return 'Upcoming';
  return 'Past';
}

function getRelativeLabel(start, end) {
  if (TODAY >= start && TODAY <= end) {
    const remaining = diffInDays(TODAY, end);
    return remaining <= 0 ? 'Ends today' : String(remaining) + 'd left';
  }

  if (TODAY < start) {
    const untilStart = diffInDays(TODAY, start);
    if (untilStart === 0) return 'Starts today';
    if (untilStart === 1) return 'Starts tomorrow';
    return 'Starts in ' + String(untilStart) + 'd';
  }

  const sinceEnd = diffInDays(end, TODAY);
  return sinceEnd <= 0 ? 'Ended today' : 'Ended ' + String(sinceEnd) + 'd ago';
}

function simplifyStrategy(strategy) {
  if (!strategy) return '';
  const [lead] = strategy.split('·');
  return lead.trim();
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function diffInDays(a, b) {
  const ms = 24 * 60 * 60 * 1000;
  return Math.round((b - a) / ms);
}

function formatMonthDay(dateStr) {
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
</script>

<style scoped>
.calendar-page {
  display: block;
}

.timeline-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.2rem 0 0.8rem;
}

.filter-tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-tab {
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.8);
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
}

.filter-tab:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.filter-tab.active {
  color: #fff;
  border-color: transparent;
  box-shadow: 0 8px 18px rgba(29, 36, 48, 0.12);
}

.filter-tab.active:not(.gold-tab):not(.silver-tab):not(.copper-tab) {
  background: var(--accent);
}

.gold-tab.active { background: #b68a2e; }
.silver-tab.active { background: #5f6b7a; }
.copper-tab.active { background: #f97316; }

.timeline-summary {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(205, 196, 183, 0.72);
  font-size: 0.78rem;
}

.summary-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-dim);
  font-size: 0.65rem;
  font-weight: 700;
}

.timeline-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-bottom: 1.1rem;
  color: var(--text-secondary);
  font-size: 0.77rem;
  font-weight: 600;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.legend-dot.buy { background: #169a59; }
.legend-dot.watch { background: #cf9220; }
.legend-dot.avoid { background: #cc4a4a; }
.legend-dot.active { background: #111827; }

.timeline-shell {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.timeline-group {
  display: flex;
  flex-direction: column;
}

.timeline-month {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.15rem 0 0.75rem;
}

.month-line {
  width: 20px;
  height: 2px;
  border-radius: 999px;
  background: var(--accent);
}

.month-label {
  font-size: 0.86rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-primary);
}

.month-meta {
  font-size: 0.76rem;
  color: var(--text-dim);
}

.timeline-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 0.7rem;
}

.timeline-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(29, 36, 48, 0.12);
  margin-top: 10px;
  z-index: 1;
}

.timeline-dot.gold { background: #b68a2e; }
.timeline-dot.silver { background: #5f6b7a; }
.timeline-dot.copper { background: #f97316; }
.timeline-dot.watch { background: #cf9220; }
.timeline-dot.avoid { background: #cc4a4a; }
.timeline-dot.active {
  box-shadow: 0 0 0 1px rgba(29, 36, 48, 0.12), 0 0 0 5px rgba(17, 24, 39, 0.08);
}

.timeline-line {
  width: 2px;
  flex: 1;
  min-height: 54px;
  background: rgba(205, 196, 183, 0.9);
  margin-top: 6px;
}

.timeline-card {
  margin: 0 0 0.65rem;
  padding: 0.75rem 0.9rem;
  border-radius: 16px;
  border: 1px solid rgba(205, 196, 183, 0.72);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 10px 22px rgba(29, 36, 48, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.timeline-card.active {
  border-color: rgba(17, 24, 39, 0.18);
  box-shadow: 0 12px 26px rgba(29, 36, 48, 0.08);
}

.timeline-card.buy { border-left: 3px solid #169a59; }
.timeline-card.watch { border-left: 3px solid #cf9220; }
.timeline-card.avoid { border-left: 3px solid #cc4a4a; }

.timeline-card-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.timeline-card-row--top {
  flex-wrap: wrap;
}

.timeline-card-row--bottom {
  align-items: flex-start;
}

.timeline-tags {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.commodity-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.24rem 0.58rem;
  border-radius: 999px;
}

.commodity-pill.gold { background: rgba(182, 138, 46, 0.12); color: #a57d23; }
.commodity-pill.silver { background: rgba(95, 107, 122, 0.12); color: #51606f; }
.commodity-pill.copper { background: rgba(249, 115, 22, 0.12); color: #e06a11; }

.pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.commodity-pill.gold .pill-dot { background: #b68a2e; }
.commodity-pill.silver .pill-dot { background: #5f6b7a; }
.commodity-pill.copper .pill-dot { background: #f97316; }

.signal-badge,
.phase-chip,
.date-span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.signal-badge {
  padding: 0.22rem 0.5rem;
}

.signal-badge.buy { background: rgba(22, 154, 89, 0.11); color: #169a59; }
.signal-badge.watch { background: rgba(207, 146, 32, 0.13); color: #b67e10; }
.signal-badge.avoid { background: rgba(204, 74, 74, 0.12); color: #c43c3c; }

.phase-chip {
  padding: 0.2rem 0.46rem;
}

.phase-chip.active { background: rgba(17, 24, 39, 0.09); color: #111827; }
.phase-chip.upcoming { background: rgba(95, 107, 122, 0.1); color: #51606f; }
.phase-chip.closed { background: rgba(138, 145, 143, 0.12); color: var(--text-secondary); }

.timeline-dates {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  flex-wrap: wrap;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 0.76rem;
}

.date-block {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.42rem;
  border-radius: 8px;
  background: rgba(248, 244, 236, 0.9);
  border: 1px solid rgba(205, 196, 183, 0.64);
}

.date-arrow {
  color: var(--text-dim);
  font-weight: 700;
}

.date-span {
  padding: 0.22rem 0.42rem;
  background: rgba(29, 36, 48, 0.06);
  color: var(--text-secondary);
}

.timeline-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
}

.timeline-copy-primary {
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
}

.timeline-copy-secondary {
  font-size: 0.76rem;
  color: var(--text-secondary);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.timeline-relative {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-dim);
  white-space: nowrap;
}

.no-events {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
}

@media (max-width: 980px) {
  .timeline-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .timeline-card-row--bottom {
    flex-direction: column;
    align-items: flex-start;
  }

  .timeline-relative {
    white-space: normal;
  }
}

@media (max-width: 640px) {
  .timeline-row {
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 0.55rem;
  }

  .timeline-card {
    padding: 0.72rem 0.75rem;
  }

  .timeline-card-row {
    gap: 0.45rem;
  }

  .timeline-dates {
    font-size: 0.72rem;
  }

  .timeline-copy-primary {
    font-size: 0.8rem;
  }

  .timeline-copy-secondary {
    -webkit-line-clamp: 2;
  }
}
</style>